/**
 * Crossing Minimization
 * 
 * Reorders nodes within each layer to minimize edge crossings.
 * Uses barycenter method with local swapping optimization.
 * 
 * ENHANCED: Groups nodes by shared descendants before ordering.
 * This ensures that chains which eventually merge are placed adjacent,
 * preventing long crossing lines from opposite sides of the graph.
 * 
 * Split into modules:
 * - descendantGrouping.js: Shared descendant analysis and grouping
 * - crossingCounting.js: Crossing counting and local swaps
 * - barycenterOrdering.js: Barycenter-based node ordering
 */

import { countCrossings, countTotalCrossings, tryLocalSwaps } from './crossingCounting';
import { orderLayerByBarycenter } from './barycenterOrdering';

// Re-export for external consumers
export { countCrossings, countTotalCrossings } from './crossingCounting';

/**
 * Minimize edge crossings using the barycenter method with local swapping
 * 
 * ENHANCED ALGORITHM:
 * 1. First, do an initial backward pass to establish group ordering based on shared descendants
 * 2. Then iterate with forward/backward passes, respecting groups
 * 3. Groups of nodes that share descendants stay together throughout
 * 
 * @param {Object} layerData - Contains layers, taskLayers, feedbackLayerInfo
 * @param {Object} graph - Dependency graph with parents/children maps
 * @returns {Object} Optimized layerData
 */
export function minimizeCrossings(layerData, graph) {
  const { layers, taskLayers, feedbackLayerInfo } = layerData;
  let optimizedLayers = layers.map(layer => [...layer]);
  let bestLayers = optimizedLayers.map(layer => [...layer]);
  let bestCrossings = countTotalCrossings(bestLayers, graph);
  
  const MAX_ITERATIONS = 4;
  
  // Initial backward pass to establish group ordering based on shared descendants
  // This is crucial for the first layer (layer 0) which has no parents to reference
  for (let i = optimizedLayers.length - 2; i >= 0; i--) {
    optimizedLayers[i] = orderLayerByBarycenter(
      optimizedLayers[i],
      optimizedLayers[i + 1],
      graph,
      'children',
      taskLayers,
      i + 1,
      i  // current layer index
    );
  }
  
  // Check if initial pass improved things
  const initialCrossings = countTotalCrossings(optimizedLayers, graph);
  if (initialCrossings < bestCrossings) {
    bestCrossings = initialCrossings;
    bestLayers = optimizedLayers.map(layer => [...layer]);
  }
  
  for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
    // Forward pass (top to bottom)
    for (let i = 1; i < optimizedLayers.length; i++) {
      optimizedLayers[i] = orderLayerByBarycenter(
        optimizedLayers[i],
        optimizedLayers[i - 1],
        graph,
        'parents',
        taskLayers,
        i - 1,
        i  // current layer index
      );
      optimizedLayers[i] = tryLocalSwaps(optimizedLayers[i], optimizedLayers[i - 1], graph);
    }
    
    // Backward pass (bottom to top)
    for (let i = optimizedLayers.length - 2; i >= 0; i--) {
      optimizedLayers[i] = orderLayerByBarycenter(
        optimizedLayers[i],
        optimizedLayers[i + 1],
        graph,
        'children',
        taskLayers,
        i + 1,
        i  // current layer index
      );
      if (i < optimizedLayers.length - 1) {
        optimizedLayers[i] = tryLocalSwaps(optimizedLayers[i], optimizedLayers[i + 1], graph);
      }
    }
    
    const currentCrossings = countTotalCrossings(optimizedLayers, graph);
    if (currentCrossings < bestCrossings) {
      bestCrossings = currentCrossings;
      bestLayers = optimizedLayers.map(layer => [...layer]);
    }
  }
  
  return { layers: bestLayers, taskLayers, feedbackLayerInfo };
}
